import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Bot, Database, Lock, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

function AgentCard({ a, active, onSelect, onOpen }) {
  return (
    <div data-testid={`systems-agent-${a.id}`} onMouseEnter={onSelect} onClick={onSelect}
      className={`p-3.5 rounded-xl border cursor-pointer transition-colors ${active ? "bg-white border-sky-300 shadow-[0_2px_8px_rgba(2,132,199,0.15)]" : "bg-white/70 border-slate-200 hover:bg-white"}`}>
      <div className="flex justify-between items-center gap-2">
        <span className="text-[13px] font-semibold text-slate-900 flex items-center gap-2"><Bot className="w-4 h-4 text-sky-600" />{a.name}</span>
        {a.status === "Planned" ? <span className="chip border-slate-200 bg-slate-50 text-slate-500">Planned</span>
          : a.allowed ? <span data-testid={`systems-agent-access-${a.id}`} className="chip border-0 ring-1 ring-emerald-200 bg-emerald-50 text-emerald-700 shrink-0">Access</span>
          : <span data-testid={`systems-agent-access-${a.id}`} className="chip border-0 ring-1 ring-red-200 bg-red-50 text-red-700 whitespace-nowrap shrink-0"><Lock className="w-3 h-3" />No access</span>}
      </div>
      <div className="text-[11px] text-slate-500 mt-1.5">{a.does}</div>
      {active && a.route && a.allowed && (
        <button data-testid={`systems-agent-open-${a.id}`} onClick={onOpen} className="mt-2 text-[11px] font-semibold text-sky-700 flex items-center gap-1 hover:text-sky-900">Open workspace <ArrowRight className="w-3 h-3" /></button>
      )}
    </div>
  );
}

function ControlLayer({ d, agent }) {
  return (
    <div className="panel p-5 space-y-4 xl:sticky xl:top-4" data-testid="systems-control-layer">
      <div className="flex items-center gap-2"><span className="icon-chip w-8 h-8 rounded-lg"><ShieldCheck className="w-4 h-4" /></span><div><div className="text-sm font-semibold text-slate-900">Control layer</div><div className="text-[11px] text-slate-500">Role-based access enforced on every request</div></div></div>
      <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 text-xs">
        <div className="text-slate-500">Signed in as</div>
        <div data-testid="systems-persona" className="font-semibold text-slate-900">{d.persona.name} · {d.persona.role}</div>
      </div>
      {agent && (
        <div data-testid="systems-check" className={`rounded-xl p-3 text-xs ring-1 ${agent.allowed ? "bg-emerald-50 ring-emerald-200 text-emerald-800" : "bg-red-50 ring-red-200 text-red-800"}`}>
          <div className="font-semibold">{agent.name}</div>
          <div className="mt-0.5">Requires <span className="num">{agent.permission}</span>: {agent.permission_label}</div>
          <div className="mt-1 font-semibold">{agent.allowed ? "Allowed for this role" : "Blocked for this role, and any attempt is logged"}</div>
        </div>
      )}
      <div className="flex gap-6"><div><div className="num text-xl font-semibold text-emerald-700" data-testid="systems-allowed">{d.control.allowed}</div><div className="text-[11px] text-slate-500">Allowed</div></div><div><div className="num text-xl font-semibold text-red-600" data-testid="systems-denied">{d.control.denied}</div><div className="text-[11px] text-slate-500">Denied</div></div></div>
      <div>
        <div className="eyebrow mb-1.5">Recent decisions</div>
        {d.control.recent.map((r, i) => <div key={i} className="flex justify-between gap-2 text-[11px] py-1 border-t border-slate-100"><span className="truncate text-slate-700">{r.actor} · {r.action}</span><span className={r.result === "Allowed" ? "text-emerald-700" : "text-red-600"}>{r.result}</span></div>)}
        {!d.control.recent.length && <div className="text-[11px] text-slate-400">No decisions logged yet.</div>}
      </div>
    </div>
  );
}

function SystemCard({ s, lit }) {
  return (
    <div data-testid={`systems-system-${s.id}`} className={`p-3.5 rounded-xl border transition-colors ${lit ? "bg-sky-50 border-sky-300" : "bg-white/70 border-slate-200"}`}>
      <div className="flex justify-between items-center gap-2">
        <span className="text-[13px] font-semibold text-slate-900 flex items-center gap-2"><Database className={`w-4 h-4 ${lit ? "text-sky-600" : "text-slate-400"}`} />{s.name}</span>
        <span className={`text-[10px] font-semibold ${s.status === "Connected" ? "text-emerald-700" : "text-slate-400"}`}>{s.status}</span>
      </div>
      <div className="text-[11px] text-slate-500 mt-1">{s.domain}</div>
      <div className="flex justify-between text-[10px] mt-1.5 num text-slate-400"><span>{s.mode}</span>{s.records > 0 && <span>{s.records} records</span>}</div>
    </div>
  );
}

export default function Systems() {
  const { personaId } = useApp();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get("/systems").then((r) => setD(r.data)); }, [personaId]);
  if (!d) return <div className="flex-1 p-8 text-sm text-slate-500">Loading systems…</div>;
  const agent = d.agents.find((a) => a.id === sel);
  return (
    <div className="flex-1 min-w-0 overflow-y-auto thin-scroll grid-bg" data-testid="systems-page">
      <div className="px-5 sm:px-8 py-8 space-y-6 max-w-[1600px]">
        <div className="rise">
          <div className="chip border-sky-200 bg-sky-50 text-sky-700 mb-3"><span className="w-1.5 h-1.5 rounded-full bg-sky-500" />Single pane of glass</div>
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight hero-title pb-1">Systems & controls</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">Staff work with agents, not platforms. Each agent reads from the systems behind it, and a role-based control layer decides who can use which agent. Point at an agent to trace its systems and check your access.</p>
        </div>
        <div className="grid xl:grid-cols-[1fr_320px_1fr] gap-5 items-start">
          <div className="space-y-2" data-testid="systems-agents"><div className="eyebrow mb-1">Agents</div>{d.agents.map((a) => <AgentCard key={a.id} a={a} active={sel === a.id} onSelect={() => setSel(a.id)} onOpen={() => nav(a.route)} />)}</div>
          <ControlLayer d={d} agent={agent} />
          <div className="space-y-2" data-testid="systems-list"><div className="eyebrow mb-1">Connected systems</div>{d.systems.map((s) => <SystemCard key={s.id} s={s} lit={agent?.systems.includes(s.id)} />)}</div>
        </div>
      </div>
    </div>
  );
}
